import React, { useState } from 'react';
import classNames from 'classnames';
import { isColorDark } from '@/utils';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { AnimatePresence, motion } from 'framer-motion';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { ModalPortal } from '@/components';
import { selectShowMenuGlobal } from '@/store/index';
import { useSelector } from 'react-redux';

const MenuItem = ({ item, className, backgroundColour, ...props }) => {
  const dividerColor = isColorDark(backgroundColour) ? 'bg-secondary-coolsand' : 'bg-primary';
  const [showEmbed, setShowEmbed] = useState(false);
  const showMenu = useSelector(selectShowMenuGlobal);

  const defaultLayoutPluginInstance = defaultLayoutPlugin({ sidebarTabs: () => [] });

  return (
    <div
      className={classNames(
        `bg-secondary-general/10 hover:bg-secondary-general/50 transition-all duration-1000 py-6 px-4
            flex justify-between
        `,
        className,
      )}
      {...props}
    >
      <div className="menu-item-details flex flex-col justify-between">
        <p
          className="font-ivy-ora text-title-s not-italic font-normal leading-32 -tracking-wider"
          role="heading"
          aria-level="3"
        >
          {item.title}
        </p>

        <div className="time-wrapper flex items-center relative">
          <p className="font-cera text-tag-m not-italic font-normal leading-24">{item.datePeriod}</p>
          {item.datePeriod && item.timePeriod && (
            <div className={classNames('divider rounded-full mt-1 w-[2px] h-[2px] mx-4', dividerColor)}></div>
          )}
          <p className="font-cera text-tag-m not-italic font-normal leading-24">{item.timePeriod}</p>
        </div>
      </div>

      <div className="menu-link hover:underline text-right tertiary-button-wrapper self-end max-w-[90px] lg:max-w-[unset]">
        <button
          onClick={() => setShowEmbed(true)}
          className={`relative text-[14px] leading-6 after:content-[''] after:absolute after:left-0 after:bottom-[3px] after:h-[1px] after:${dividerColor} after:w-0 after:transition-all after:duration-300 hover:after:w-full`}
        >
          {item.ctalink?.text}
        </button>
      </div>

      <ModalPortal>
        <AnimatePresence>
          {showEmbed && (
            <motion.div
              className={classNames(
                'disable-scroll fixed lg:border-b-[10px] lg:border-b-secondary-coolsand top-0  z-50 flex h-screen w-screen flex-col items-center justify-center bg-black/70 backdrop-blur-[6px]',
                showMenu ? 'lg:top-[104px] lg:h-[calc(100vh-104px)]' : ' lg:top-0',
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className={classNames(
                  'bg-white pdf-modal-wrapper md:px-10 rounded-lg w-[90%] h-[calc(100vh-70px)]  relative shadow-lg',
                  showMenu ? 'lg:h-[calc(100vh-130px)]' : 'lg:h-[calc(100vh-70px)]',
                )}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <button
                  onClick={() => setShowEmbed(false)}
                  className="absolute top-2 right-2 text-primary bg-white border border-primary z-10 w-[93px] h-[44px] px-3 py-1"
                >
                  <span>✕ Close</span>
                </button>

                <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
                  <div
                    className="h-full overflow-auto pdf-zoom-container"
                    style={{ touchAction: 'pan-y pinch-zoom', WebkitOverflowScrolling: 'touch' }}
                  >
                    <Viewer
                      fileUrl={item.ctalink?.href}
                      plugins={[defaultLayoutPluginInstance]}
                      className="pdf-zoom-container"
                      style={{ touchAction: 'pan-y pinch-zoom', WebkitOverflowScrolling: 'touch' }}
                    />
                  </div>
                </Worker>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalPortal>
    </div>
  );
};

export default MenuItem;
